import { useState } from "react";
import { Calculator, Loader2, ArrowLeft } from "lucide-react";
import api from "../../lib/api";

const METHODS = [
  { value: "container_20", label: "20ft Container" },
  { value: "container_40", label: "40ft Container" },
  { value: "roro", label: "RoRo (Roll-on/Roll-off)" },
];
const TYPES = [
  { value: "sedan", label: "Sedan / Saloon" },
  { value: "suv", label: "SUV / Crossover" },
  { value: "pickup", label: "Pickup" },
  { value: "truck", label: "Truck" },
  { value: "van", label: "Van / Bus" },
  { value: "other", label: "Other" },
];

const money = (n) => "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

export const EstimatorForm = ({ onBack }) => {
  const [form, setForm] = useState({ vehicle_value: "", shipping_method: "container_20", vehicle_type: "sedan" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_value) return;
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/estimate", {
        ...form,
        vehicle_value: Number(form.vehicle_value),
      });
      setResult(data);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4" data-testid="assistant-estimator">
      <button onClick={onBack} className="mb-3 inline-flex items-center gap-1.5 text-xs font-bold text-navy hover:text-brandred" data-testid="estimator-back">
        <ArrowLeft size={14} /> Back to chat
      </button>
      <div className="mb-3 flex items-center gap-2">
        <Calculator size={18} className="text-brandred" />
        <h3 className="font-display text-base font-black text-navy">Instant Import-Cost Estimate</h3>
      </div>

      {!result ? (
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-bold text-navy">Vehicle value (USD)</label>
            <input
              type="number" min="0" required inputMode="numeric"
              value={form.vehicle_value}
              onChange={(e) => setForm({ ...form, vehicle_value: e.target.value })}
              placeholder="e.g. 15000"
              data-testid="estimator-value"
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-navy"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold text-navy">Shipping method</label>
            <select
              value={form.shipping_method}
              onChange={(e) => setForm({ ...form, shipping_method: e.target.value })}
              data-testid="estimator-method"
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-navy"
            >
              {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold text-navy">Vehicle type</label>
            <select
              value={form.vehicle_type}
              onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
              data-testid="estimator-type"
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-navy"
            >
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <button
            type="submit" disabled={loading}
            data-testid="estimator-submit"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brandred px-4 py-3 text-sm font-bold text-white hover:bg-brandred-700 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Calculator size={16} />}
            Calculate estimate
          </button>
        </form>
      ) : (
        <div data-testid="estimator-result">
          <div className="rounded-xl bg-navy p-4 text-white">
            <p className="text-xs uppercase tracking-widest text-gold">Estimated landed cost</p>
            <p className="mt-1 font-display text-3xl font-black">{money(result.estimated_total_landed_cost)}</p>
          </div>
          <div className="mt-3 space-y-1.5 rounded-xl border border-gray-100 bg-gray-50 p-3">
            {Object.entries(result.breakdown).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-semibold text-navy">{money(v)}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] leading-snug text-muted-foreground">{result.disclaimer}</p>
          <button
            onClick={() => setResult(null)}
            className="mt-3 w-full rounded-xl border border-navy/20 px-4 py-2.5 text-sm font-bold text-navy hover:bg-navy-50"
            data-testid="estimator-recalc"
          >
            Recalculate
          </button>
        </div>
      )}
    </div>
  );
};
